import {
	IconButton,
	IDetailsList,
	mergeStyleSets,
	Stack,
	Text,
	useTheme,
} from '@fluentui/react';
import React, {useCallback, useMemo} from 'react';
import {EntityListColumn, EntityListFilter} from './EntityList';
import {v4 as uuidv4} from 'uuid';
import value from 'require-context.macro';
import {formatDateTime} from 'i18n/localeDateFormat';
import {useTranslation} from 'react-i18next';
import {FilterOption} from 'types';

type FilterBubbleProps = {
	relevantFilters: EntityListFilter[];
	colName: string;
	fieldValuesUnique?: FilterOption[];
	onBubbleClick: (
		filterToRemove: EntityListFilter,
	) => (_event: React.MouseEvent<HTMLElement>) => void;
};
type EntityListFilterBubblesProps = {
	columns: EntityListColumn[];
	appliedFilters: EntityListFilter[];
	setAppliedFilters: (appliedFilters: EntityListFilter[]) => void;
	setSelectedFilters: (selectedFilters: EntityListFilter[]) => void;
	listRef: React.MutableRefObject<IDetailsList | undefined>;
};

export const EntityListFilterBubbles: React.FC<
	EntityListFilterBubblesProps
> = ({
	columns,
	appliedFilters,
	listRef,
	setAppliedFilters,
	setSelectedFilters,
}) => {
	const arrFieldValuesUnique = useMemo(() => [], [columns]);

	const onBubbleClick = useCallback(
		(filterToRemove: EntityListFilter) =>
			(_event: React.MouseEvent<HTMLElement>): void => {
				const newFilter = appliedFilters.filter(
					x =>
						!(
							x.columnKey === filterToRemove.columnKey &&
							x.selection === filterToRemove.selection
						),
				);
				setSelectedFilters(newFilter);
				setAppliedFilters(newFilter);
				listRef.current?.scrollToIndex(0);
			},
		[appliedFilters, listRef.current],
	);
	return (
		<>
			{appliedFilters.length > 0 && (
				<Stack horizontal tokens={{childrenGap: 20}}>
					{columns.map((x, i) => {
						const relevantFilters = appliedFilters.filter(
							y => y.columnKey === x.key,
						);
						if (relevantFilters.length > 0) {
							return (
								<FilterBubble
									key={uuidv4()}
									colName={x.name}
									fieldValuesUnique={arrFieldValuesUnique.at(i)}
									relevantFilters={relevantFilters}
									onBubbleClick={onBubbleClick}
								/>
							);
						}
						return <></>;
					})}
				</Stack>
			)}
		</>
	);
};

export const FilterBubble: React.FC<FilterBubbleProps> = ({
	relevantFilters,
	colName,
	fieldValuesUnique,
	onBubbleClick,
}) => {
	const theme = useTheme();

	const {i18n} = useTranslation();

	const classNames = mergeStyleSets({
		arrayWrapper: {
			display: 'flex !important',
			flexWrap: 'wrap',
		},
		arrayItem: {
			borderRadius: '10px',
			color: theme?.palette.neutralSecondary,
			background: theme?.palette.neutralLight,
			fontSize: 12,
			padding: '2px 7px',
			marginRight: 5,
			marginBottom: 10,
			whiteSpace: 'nowrap',
		},
		arrayButton: {
			cursor: 'pointer',
		},
	});
	return (
		<div className={classNames.arrayWrapper}>
			<div className={classNames.arrayWrapper}>
				{colName}:&nbsp;
				{relevantFilters
					.filter(
						x =>
							x.display !== 'UNKNOWN' ||
							fieldValuesUnique?.find(y => y.optionKey === x.selection),
					)
					.map((value, index) => (
						<div key={uuidv4()} className={classNames.arrayItem}>
							<Text key={uuidv4()} title={value.columnKey}>
								{value.display === 'UNKNOWN'
									? fieldValuesUnique?.find(
											y => y.optionKey === value.selection,
									  )?.name
									: value.display}
								&nbsp;
							</Text>
							<IconButton
								iconProps={{iconName: 'ChromeClose'}}
								styles={{
									root: {
										width: 18,
										height: 18,
										color: 'black',
									},
									icon: {fontSize: 9},
								}}
								onClick={onBubbleClick(value)}
							></IconButton>
						</div>
					))}
			</div>
		</div>
	);
};
